import { translateBackend } from '@assets/i18n/config';
import type {
  AssetClassBranch,
  PortfolioTemplate,
} from '@interfaces/financialProduct';
import {
  ASSET_CLASS_FIELD_BY_LEVEL,
  AssetClassLevel,
} from '@interfaces/financialProduct';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import {
  CHART_COLOR_BY_SUPPORT,
  CHART_COLOR_VARIANT_BY_SUPPORT,
} from '@ovComponents/2-component/pieChart/getChartColors';
import { PieChart } from '@ovComponents/2-component/pieChart/pieChart';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { isEmpty, isUndefined } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Security } from '../securitiesSelectionTable/securitiesSelectionTable';
import { SelectionChip } from '../selectionChip/selectionChip';

type AssetClassGroup = {
  totalPercentage: number;
  name: string;
  color: string;
  templateColor: string;
  templateTargetPercentage: number;
};

type PieChartSecuritiesProps = {
  securities: Security[];
  variant?: 'elevated' | 'outlined' | 'none';
  modelPortfolioName?: string;
  portfolioTemplate?: PortfolioTemplate;
  testId?: string;
  assetClassLevel?: AssetClassLevel | string;
};

type PieChartData = {
  pieChartData: number[];
  pieChartColors: string[];
};

const DEFAULT_BORDER_RADIUS = '4px';
const DEFAULT_COLOR = '#FFFFFF';
const DEFAULT_TEXT_COLOR = '#17181C';
const COLOR_LIST = [
  'supportOne',
  'supportTwo',
  'supportThree',
  'supportFour',
  'supportFive',
  'supportSix',
];

const ensureString = (value: unknown, defaultValue: string): string => {
  if (typeof value === 'string' && value !== '') {
    return value;
  }
  return defaultValue;
};

const assignColorsToAssetClasses = (
  assetClasses: string[],
): Record<string, string> =>
  assetClasses.reduce<Record<string, string>>(
    (acc, assetClass, i) => ({
      ...acc,
      [assetClass]: COLOR_LIST[i % COLOR_LIST.length],
    }),
    {},
  );

const formatPercentage = (value: number): string =>
  `${Number(value).toFixed(2)}%`;

const AssetClassLegendItem = ({
  dataTestId,
  item,
  isActive,
  isLast,
  color,
  surfaceContainerColor,
  surfaceColor,
  borderRadius,
  onMouseEnter,
  onMouseLeave,
}: {
  dataTestId: string;
  item: AssetClassGroup;
  isActive: boolean;
  isLast: boolean;
  color: string;
  surfaceContainerColor: string;
  surfaceColor: string;
  borderRadius: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => (
  <Box
    data-testid={dataTestId}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'left',

      borderBottom:
        !isActive && !isLast
          ? `2px solid ${surfaceContainerColor}`
          : `2px solid ${surfaceColor}`,

      py: 0.5,
      px: 1,
      backgroundColor: isActive ? surfaceContainerColor : 'none',
      borderRadius: isActive ? borderRadius : '0px',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'start',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          borderRadius: '10px',
          height: '8px',
          width: '8px',
          backgroundColor: color,
          mr: 1,
        }}
      />
      <Typography
        variant='bodyLarge'
        data-testid={`${dataTestId}-legend-item-name`}
        sx={{ fontSize: '0.75rem' }}
      >
        {item.templateTargetPercentage &&
        item.totalPercentage !== item.templateTargetPercentage
          ? `${item.name} (${item.templateTargetPercentage}%)`
          : item.name}
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography
        variant='bodyLarge'
        data-testid={`${dataTestId}-legend-item-percentage`}
        sx={{
          fontSize: '0.75rem',
          color:
            item.templateTargetPercentage &&
            item.totalPercentage !== item.templateTargetPercentage
              ? '#D32F2F'
              : DEFAULT_TEXT_COLOR,
        }}
      >
        {formatPercentage(item.totalPercentage || 0)}
      </Typography>
    </Box>
  </Box>
);

export const PieChartSecurities = ({
  securities = [],
  variant = 'none',
  modelPortfolioName = '',
  portfolioTemplate,
  testId = 'pie-chart-securities',
  assetClassLevel,
}: PieChartSecuritiesProps) => {
  const [activeItem, setActiveItem] = useState<AssetClassGroup | null>(null);
  const theme = useTheme();
  const { t } = useTranslation([
    'shared',
    'modelPortfolios',
    'client',
    'buildModels',
  ]);

  const colorsSecurities = CHART_COLOR_BY_SUPPORT;
  const colorsAssetClassTemplate = CHART_COLOR_VARIANT_BY_SUPPORT;

  const portfolioTemplateAssetClassBranches = useMemo(() => {
    if (!portfolioTemplate?.assetClassBranches) {
      return [];
    }

    const resolveBranchName = (branch: AssetClassBranch): string =>
      translateBackend(branch.assetClass.translatedName) ||
      branch.assetClass.name ||
      '';

    const assetClassNames =
      portfolioTemplate.assetClassBranches.map(resolveBranchName);

    const colorMap = assignColorsToAssetClasses(
      assetClassNames.filter((name) => name !== ''),
    );

    return portfolioTemplate.assetClassBranches.map((branch) => {
      const name = resolveBranchName(branch);
      return {
        name,
        templateColor: colorMap[name] || 'supportOne',
        templateTargetPercentage: branch.targetPercentage || 0,
      };
    });
  }, [portfolioTemplate]);

  const securitiesDataState = useMemo(() => {
    const isDummyData =
      securities.length === 1 &&
      !securities[0].financialProduct.primaryAssetClass?.translatedName;
    const hasData = !isEmpty(securities) && !isDummyData;
    return { isDummyData, hasData };
  }, [securities]);

  const resolvedAssetClassField = useMemo(
    () =>
      ASSET_CLASS_FIELD_BY_LEVEL[
        (assetClassLevel as AssetClassLevel) ?? AssetClassLevel.PRIMARY
      ] ?? ASSET_CLASS_FIELD_BY_LEVEL[AssetClassLevel.PRIMARY],
    [assetClassLevel],
  );

  const assetClassData = useMemo(() => {
    const templateColorMap = portfolioTemplateAssetClassBranches.reduce(
      (acc: Record<string, string>, branch) => {
        if (branch.name) {
          acc[branch.name] = branch.templateColor;
        }
        return acc;
      },
      {},
    );

    const getAssetClassName = (security: Security): string => {
      const assetClassObj = security.financialProduct[
        resolvedAssetClassField
      ] as
        | { translatedName?: Record<string, string>; name?: string }
        | undefined;
      return (
        translateBackend(assetClassObj?.translatedName) ||
        assetClassObj?.name ||
        t('buildModels:other')
      );
    };

    // Create a map of asset class to color from securities
    const assetClassColorMap = securities.reduce(
      (acc: Record<string, string>, security: Security) => {
        const assetClass = getAssetClassName(security);
        if (!acc[assetClass] && security.color) {
          acc[assetClass] = security.color;
        }
        return acc;
      },
      {},
    );

    const assetClassGroups = securities.reduce(
      (acc: Record<string, AssetClassGroup>, security: Security) => {
        const assetClass = getAssetClassName(security);
        const templateBranch = portfolioTemplateAssetClassBranches.find(
          (b) => b.name === assetClass,
        );

        if (acc[assetClass]) {
          acc[assetClass].totalPercentage += security.percentage || 0;
        } else {
          acc[assetClass] = {
            totalPercentage: security.percentage || 0,
            name: assetClass,
            color:
              assetClassColorMap[assetClass] ||
              templateColorMap[assetClass] ||
              'supportOne',
            templateColor:
              templateBranch?.templateColor ||
              templateColorMap[assetClass] ||
              'supportOne',
            templateTargetPercentage:
              templateBranch?.templateTargetPercentage || 0,
          };
        }
        return acc;
      },
      {},
    );

    portfolioTemplateAssetClassBranches.forEach((branch) => {
      if (branch.name && !assetClassGroups[branch.name]) {
        assetClassGroups[branch.name] = {
          // When there is no actual securities data, use the template target as
          // the displayed value so the legend shows the intended allocation.
          totalPercentage: securitiesDataState.hasData
            ? 0
            : branch.templateTargetPercentage,
          name: branch.name,
          color: templateColorMap[branch.name] || 'supportOne',
          templateColor: branch.templateColor,
          templateTargetPercentage: branch.templateTargetPercentage,
        };
      }
    });

    return Object.values(assetClassGroups).sort(
      (a, b) => b.totalPercentage - a.totalPercentage,
    );
  }, [
    securities,
    portfolioTemplateAssetClassBranches,
    resolvedAssetClassField,
    securitiesDataState,
    t,
  ]);

  const pieChartDataAndColors = useMemo((): PieChartData => {
    if (securitiesDataState.hasData) {
      const totalPercentage = assetClassData.reduce(
        (acc: number, x) => acc + Number(x.totalPercentage),
        0,
      );
      const remainingPercentage = Math.max(0, 100 - totalPercentage);

      return {
        pieChartData: [
          ...assetClassData.map((x) => Number(x.totalPercentage)),
          remainingPercentage,
        ],
        pieChartColors: [
          ...assetClassData.map((x) =>
            ensureString(
              colorsSecurities[x.color as keyof typeof colorsSecurities],
              colorsSecurities.supportOne,
            ),
          ),
          ensureString(theme.color.surfaceContainer, DEFAULT_COLOR),
        ],
      };
    }

    if (portfolioTemplateAssetClassBranches.length) {
      const templateData = assetClassData.filter(
        (x) => x.templateTargetPercentage > 0,
      );
      const totalPercentage = templateData.reduce(
        (acc: number, x) => acc + Number(x.totalPercentage),
        0,
      );
      const remainingPercentage = Math.max(0, 100 - totalPercentage);

      return {
        pieChartData: [
          ...templateData.map((x) => Number(x.totalPercentage)),
          remainingPercentage,
        ],
        pieChartColors: [
          ...templateData.map((x) =>
            ensureString(
              colorsAssetClassTemplate[
                x.templateColor as keyof typeof colorsAssetClassTemplate
              ],
              colorsAssetClassTemplate.supportOne,
            ),
          ),
          ensureString(theme.color.surfaceContainer, DEFAULT_COLOR),
        ],
      };
    }

    return {
      pieChartData: [100],
      pieChartColors: [
        ensureString(theme.color.surfaceContainer, DEFAULT_COLOR),
      ],
    };
  }, [
    assetClassData,
    colorsSecurities,
    colorsAssetClassTemplate,
    theme.color.surfaceContainer,
    securitiesDataState.hasData,
    portfolioTemplateAssetClassBranches,
  ]);

  const renderCenterContent = () => {
    if (
      !securitiesDataState.hasData &&
      !portfolioTemplateAssetClassBranches.length
    ) {
      return (
        <>
          <Typography
            variant='bodyLarge'
            data-testid={`${testId}-pie-chart-no-data`}
            sx={{
              color: theme.color.onSurfaceVariant,
              fontSize: '0.875rem',
            }}
          >
            {modelPortfolioName}
          </Typography>
          <Typography
            variant='bodyLarge'
            data-testid={`${testId}-pie-chart-no-data-percentage`}
            sx={{ fontSize: '0.875rem', fontWeight: 500 }}
          >
            0.00%
          </Typography>
        </>
      );
    }

    if (activeItem) {
      return (
        <>
          <Typography
            variant='bodyLarge'
            data-testid={`${testId}-pie-chart-active-item`}
            sx={{
              color: theme.color.onSurfaceVariant,
              fontSize: '0.875rem',
            }}
          >
            {activeItem.templateTargetPercentage &&
            activeItem.totalPercentage !== activeItem.templateTargetPercentage
              ? `${activeItem.name} (${activeItem.templateTargetPercentage}%)`
              : activeItem.name}
          </Typography>
          <Typography
            variant='bodyLarge'
            data-testid={`${testId}-pie-chart-active-item-percentage`}
            sx={{ fontSize: '0.875rem', fontWeight: 500 }}
          >
            {formatPercentage(activeItem.totalPercentage)}
          </Typography>
        </>
      );
    }

    return (
      <>
        <Typography
          variant='bodyLarge'
          data-testid={`${testId}-pie-chart-no-data-name`}
          sx={{
            color: theme.color.onSurfaceVariant,
            fontSize: '0.875rem',
          }}
        >
          {modelPortfolioName}
        </Typography>
        <Typography
          variant='bodyLarge'
          data-testid={`${testId}-pie-chart-no-data-percentage`}
          sx={{ fontSize: '0.875rem', fontWeight: 500 }}
        >
          {`${assetClassData.reduce((acc: number, x) => acc + Number(x.totalPercentage), 0).toFixed(2)}%`}
        </Typography>
      </>
    );
  };

  return (
    <Card variant={variant}>
      <CardContent sx={{ p: '14px !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <SelectionChip
              onChange={() => {}}
              value={assetClassLevel ?? AssetClassLevel.PRIMARY}
              showCheckIcon={false}
              options={[
                {
                  value: assetClassLevel ?? AssetClassLevel.PRIMARY,
                  label: t(
                    `modelPortfolios:${assetClassLevel ?? AssetClassLevel.PRIMARY}`,
                  ),
                  dataTestId: `${testId}-selection-chip-asset-class-level`,
                },
              ]}
            />
          </Box>
          <Box
            sx={{
              position: 'relative',
              height: 270,
              mb: 0.5,
            }}
          >
            <PieChart
              testId={`${testId}-pie-chart`}
              datasets={[
                {
                  label: '',
                  data: pieChartDataAndColors.pieChartData,
                },
              ]}
              labels={assetClassData.map((x) => x.name)}
              onHover={(index: number | undefined) => {
                if (!isUndefined(index) && index < assetClassData.length) {
                  setActiveItem(assetClassData[index]);
                } else {
                  setActiveItem(null);
                }
              }}
              customColors={pieChartDataAndColors.pieChartColors}
              cutoutPercentage={80}
            />
            <Box
              sx={{
                textAlign: 'center',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%,-50%)',
              }}
            >
              {renderCenterContent()}
            </Box>
          </Box>

          {securitiesDataState.hasData ||
          portfolioTemplateAssetClassBranches.length ? (
            <Box>
              {assetClassData.map((item, i) => (
                <AssetClassLegendItem
                  dataTestId={`${testId}-pie-chart-legend-item`}
                  key={item.name}
                  item={item}
                  isActive={activeItem?.name === item.name}
                  isLast={i + 1 === assetClassData.length}
                  color={
                    securitiesDataState.hasData
                      ? ensureString(
                          colorsSecurities[
                            item.color as keyof typeof colorsSecurities
                          ],
                          colorsSecurities.supportOne,
                        )
                      : ensureString(
                          colorsAssetClassTemplate[
                            item.templateColor as keyof typeof colorsAssetClassTemplate
                          ],
                          colorsAssetClassTemplate.supportOne,
                        )
                  }
                  surfaceContainerColor={ensureString(
                    theme.color.surfaceContainer,
                    DEFAULT_COLOR,
                  )}
                  surfaceColor={ensureString(
                    theme.color.surface,
                    DEFAULT_COLOR,
                  )}
                  borderRadius={ensureString(
                    theme.borderRadius.md,
                    DEFAULT_BORDER_RADIUS,
                  )}
                  onMouseEnter={() => setActiveItem(item)}
                  onMouseLeave={() => setActiveItem(null)}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'left',
                py: 0.5,
                px: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'start',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    borderRadius: '10px',
                    height: '8px',
                    width: '8px',
                    backgroundColor: theme.color.surfaceContainer,
                    mr: 1,
                  }}
                />
                <Typography
                  variant='bodyLarge'
                  data-testid={`${testId}-pie-chart-legend-item-name`}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {t('modelPortfolios:modelPortfolioTable.assetClass')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography
                  variant='bodyLarge'
                  data-testid={`${testId}-pie-chart-legend-item-percentage`}
                  sx={{ fontSize: '0.75rem', color: DEFAULT_TEXT_COLOR }}
                >
                  --%
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
